import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { HoldingsInstitution } from "@/api/types";
import { useHoldings } from "@/api/hooks";
import {
  BackLink,
  Breadcrumb,
  Chip,
  type ChipTone,
  EditorialInput,
} from "@/components/shared";
import { INSTITUTION_LABELS } from "@/components/graph/institutionColors";

export const Route = createFileRoute("/institution/$institutionId")({
  component: InstitutionPage,
});

// Backend authorship vocabulary → the frontend's five-class badge set, mirroring
// the catalog's mapping; anything unmapped is dropped rather than mis-rendered.
const AUTH_META: Array<{ classes: string[]; label: string; tone: ChipTone }> = [
  { classes: ["authored"], label: "Authored", tone: "authored" },
  { classes: ["edited"], label: "Edited", tone: "edited" },
  { classes: ["recipient"], label: "Recipient", tone: "neutral" },
  { classes: ["mentions", "mentioned_contemporary"], label: "Mentions", tone: "mentions" },
  { classes: ["memorialized"], label: "Memorialized", tone: "memorialized" },
];

function InstitutionPage() {
  const { institutionId } = Route.useParams();
  const { data, isLoading } = useHoldings("institution");

  const inst = data?.holdings.find((h) => h.institution_id === institutionId);

  // Any institution the holdings endpoint surfaces gets a detail page, including
  // one-off sources — except User Upload, a user-contributed source with no
  // public page.
  const blocked = institutionId === "USER_UPLOAD";

  if (isLoading && !data) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
        <InstitutionSkeleton />
      </div>
    );
  }

  if (blocked || !inst) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <p className="text-caps uppercase tracking-caps text-ink-tertiary">Not found</p>
        <h1 className="mt-3 font-serif text-display text-ink-primary">
          No institution “{INSTITUTION_LABELS[institutionId] ?? institutionId}”
        </h1>
        <p className="mx-auto mt-3 max-w-md font-serif text-lede italic text-ink-secondary">
          That institution isn’t one of the surveyed holdings.
        </p>
        <BackLink
          fallback={{ to: "/", search: { mode: "catalog" } }}
          className="mt-6 inline-block text-meta text-ink-info hover:text-ink-primary"
        >
          ← Back to the catalog
        </BackLink>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
      <InstitutionDetail inst={inst} />
    </div>
  );
}

function InstitutionDetail({ inst }: { inst: HoldingsInstitution }) {
  return (
    <section className="qm-fade-in flex flex-col gap-8">
      <Breadcrumb
        segments={[
          { label: "Corpus", to: "/", search: { mode: "catalog" } },
          { label: "Catalog", to: "/", search: { mode: "catalog" } },
          { label: inst.institution_name },
        ]}
      />

      <header className="flex flex-col gap-2">
        <p className="text-caps uppercase tracking-caps text-ink-tertiary">Institution</p>
        <h1 className="font-serif text-display leading-tight text-ink-primary">
          {inst.institution_name}
        </h1>
        <p className="font-serif text-lede italic leading-relaxed text-ink-secondary">
          Structural intelligence across this institution’s holdings — a verified knowledge graph,
          organized by who holds it.
        </p>
      </header>

      {/* Structural-intelligence stats — the same triad the catalog cards show. */}
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard value={inst.document_count} label="documents" />
        <StatCard value={inst.claim_count} label="verified claims" />
        <StatCard value={inst.entity_count} label="distinct entities" />
      </dl>

      <AuthorshipRow breakdown={inst.authorship_breakdown} />

      <ExploreBox institutionId={inst.institution_id} institutionName={inst.institution_name} />
    </section>
  );
}

function StatCard({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-card border border-rule bg-surface-card px-5 py-4">
      <dd className="font-serif text-display leading-none text-ink-primary tabular-nums">
        {value.toLocaleString()}
      </dd>
      <dt className="mt-2 text-caps uppercase tracking-badge text-ink-tertiary">{label}</dt>
    </div>
  );
}

function AuthorshipRow({
  breakdown,
}: {
  breakdown: HoldingsInstitution["authorship_breakdown"];
}) {
  // Fold the backend's richer vocabulary onto the five-class badge set.
  const counts = new Map<string, number>();
  for (const b of breakdown) counts.set(b.authorship_class, (counts.get(b.authorship_class) ?? 0) + b.count);
  const chips = AUTH_META.map(({ classes, label, tone }) => {
    const n = classes.reduce((sum, c) => sum + (counts.get(c) ?? 0), 0);
    return n > 0 ? { label, tone, n } : null;
  }).filter((c): c is { label: string; tone: ChipTone; n: number } => c !== null);

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 border-t border-rule pt-5">
      <p className="text-caps uppercase tracking-caps text-ink-tertiary">Authorship distribution</p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map(({ label, tone, n }) => (
          <Chip key={label} tone={tone}>
            {label}
            <span className="ml-1 tabular-nums opacity-80">{n.toLocaleString()}</span>
          </Chip>
        ))}
      </div>
    </div>
  );
}

/** Institution-scoped "ask the corpus" box — mirrors the finding-aid and
 *  catalog ask boxes, but threads the institution slug so the answer is
 *  grounded only in this institution's holdings. */
function ExploreBox({
  institutionId,
  institutionName,
}: {
  institutionId: string;
  institutionName: string;
}) {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-2 border-t border-rule pt-6">
      <p className="font-serif text-meta italic text-ink-tertiary">
        Explore {institutionName} — ask a question answered only from its holdings.
      </p>
      <EditorialInput
        value={value}
        onChange={setValue}
        onSubmit={() => {
          const q = value.trim();
          if (!q) return;
          navigate({ to: "/search", search: { q, mode: "search", institution: institutionId } });
        }}
        placeholder={`Ask about ${institutionName}…`}
        ariaLabel={`Ask about ${institutionName}`}
        submitLabel="Ask"
      />
    </div>
  );
}

function InstitutionSkeleton() {
  return (
    <div className="flex flex-col gap-8" aria-live="polite" aria-busy="true">
      <div className="h-4 w-1/3 animate-pulse rounded bg-rule/20" />
      <div className="h-9 w-2/3 animate-pulse rounded bg-rule/30" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-card bg-rule/20" />
        ))}
      </div>
      <span className="sr-only">Loading institution…</span>
    </div>
  );
}
